import React from "react";
import {
  FaLaptopCode,
  FaShieldAlt,
  FaServer,
  FaDatabase,
  FaNetworkWired,
  FaCloud,
  FaTools,
  FaUniversity,
  FaUsers
} from "react-icons/fa";

const skillCategories = [
  {
    track: "Track A: Software & Data Architecture",
    categories: [
      {
        title: "Software Development",
        icon: <FaLaptopCode />,
        skills: [
          { name: "JavaScript (ES6+)", desc: "Modern ECMAScript standards and async programming" },
          { name: "React.js", desc: "Component architecture, hooks, state management" },
          { name: "Next.js", desc: "Server-side rendering, App Router, static generation" },
          { name: "Node.js", desc: "Asynchronous backend runtimes and server development" },
          { name: "HTML5 & CSS3", desc: "Semantic markup, modern layout, responsive design" },
          { name: "API Integration", desc: "RESTful consumption, error handling, rate limiting" },
          { name: "PWAs", desc: "Service workers, offline caching, installable apps" }
        ]
      },
      {
        title: "Database Management",
        icon: <FaDatabase />,
        skills: [
          { name: "SQL", desc: "Relational database querying and schema architecture" },
          { name: "PostgreSQL", desc: "Advanced queries, RLS security policies, stored procedures" },
          { name: "PostGIS", desc: "Spatial indexes and geospatial query execution" },
          { name: "MongoDB", desc: "Document-based data models and aggregation pipelines" },
          { name: "Query Optimization", desc: "Indexing strategies and execution plan tuning" }
        ]
      },
      {
        title: "Tools & Methods",
        icon: <FaTools />,
        skills: [
          { name: "Git / GitHub", desc: "Version control, branching workflows, pull requests" },
          { name: "Jira (Agile/Scrum)", desc: "Sprint planning, user stories, backlog management" },
          { name: "Postman", desc: "API testing, collections, environment variables" },
          { name: "Leaflet Maps", desc: "Interactive mapping integration and vector layers" },
          { name: "SDLC", desc: "Full software development lifecycle governance" },
          { name: "MS Excel (Advanced)", desc: "Data modeling, formulas, analytics" },
          { name: "Data Visualisation", desc: "Presenting metrics and structured insights" }
        ]
      }
    ]
  },
  {
    track: "Track B: Infrastructure & Cybersecurity",
    categories: [
      {
        title: "IT Support & Sysadmin",
        icon: <FaServer />,
        skills: [
          { name: "Windows 10/11 & Server", desc: "Enterprise operating systems administration" },
          { name: "Hardware/Software T-Shooting", desc: "Diagnostic isolation and hardware repair" },
          { name: "Active Directory", desc: "User accounts, group policies, access delegation" },
          { name: "System Maintenance", desc: "Patch management, automated updates, backups" },
          { name: "Network Configuration", desc: "Host IP configuration, DNS/DHCP settings" }
        ]
      },
      {
        title: "Cybersecurity",
        icon: <FaShieldAlt />,
        skills: [
          { name: "Network Security Monitoring", desc: "Traffic analysis and anomaly detection" },
          { name: "Wireshark", desc: "Deep packet inspection and protocol analysis" },
          { name: "Nmap", desc: "Network discovery and vulnerability scanning" },
          { name: "Metasploit", desc: "Exploit verification and security auditing" },
          { name: "Burp Suite", desc: "Web app security testing and proxy inspection" },
          { name: "IAM & CIA Triad", desc: "Access control, confidentiality, integrity, availability" },
          { name: "Firewalls & VPNs", desc: "Perimeter defense and encrypted remote tunnels" }
        ]
      },
      {
        title: "Networking",
        icon: <FaNetworkWired />,
        skills: [
          { name: "TCP/IP & OSI Model", desc: "Fundamental network protocols and layering" },
          { name: "DNS & DHCP", desc: "Name resolution and automated IP address assignment" },
          { name: "LAN / WAN", desc: "Local and wide area network topology design" },
          { name: "Patch Panel Management", desc: "Structured cabling and rack organization" },
          { name: "Network Troubleshooting", desc: "Tracing packet loss, connectivity, and latency" }
        ]
      },
      {
        title: "Banking & Finance IT",
        icon: <FaUniversity />,
        skills: [
          { name: "Core Banking Support", desc: "Financial application maintenance and triage" },
          { name: "Payment Processing", desc: "Transaction workflows and verification concepts" },
          { name: "Banking Security Compliance", desc: "Strict data privacy and regulatory compliance" },
          { name: "Enterprise IT Operations", desc: "High-uptime corporate IT service delivery" }
        ]
      },
      {
        title: "DevOps & Cloud",
        icon: <FaCloud />,
        skills: [
          { name: "AWS Cloud", desc: "Cloud computing fundamentals, S3, EC2, IAM" },
          { name: "Docker", desc: "Containerization and lightweight runtime packaging" },
          { name: "Kubernetes (Basic)", desc: "Container orchestration and pod deployment" },
          { name: "VMware & Hyper-V", desc: "Hypervisors and virtual machine lifecycle management" },
          { name: "CI/CD Pipelines", desc: "Automated build, test, and release workflows" }
        ]
      }
    ]
  },
  {
    track: "Professional Competencies",
    categories: [
      {
        title: "Soft Skills & Leadership",
        icon: <FaUsers />,
        skills: [
          { name: "Cross-Functional Leadership", desc: "Leading multi-disciplinary dev and design teams" },
          { name: "Technical Communication", desc: "Translating complex IT concepts clearly" },
          { name: "Problem Solving", desc: "Root cause analysis under production constraints" },
          { name: "Documentation", desc: "Standard operating procedures and runbooks" },
          { name: "Customer Service", desc: "Empathetic, efficient IT user support" }
        ]
      }
    ]
  }
];

function Skills() {
  return (
    <section id="skills" className="skills">
      <div className="container">
        <div className="section-header">
          <h2 className="section-title">Technical Skills & Expertise</h2>
          <p className="section-subtitle">
            Structured competency matrix across software engineering disciplines, systems administration, network engineering, and cybersecurity.
          </p>
        </div>

        {skillCategories.map((group, idx) => (
          <div key={idx} className="skills-track-group">
            <div className="skills-track-title">{group.track}</div>
            <div className="skills-cards">
              {group.categories.map((cat, i) => (
                <div key={i} className="skills-card">
                  <div className="skills-card-head">
                    <div className="skills-icon">{cat.icon}</div>
                    <h3>{cat.title}</h3>
                  </div>
                  <div className="badges">
                    {cat.skills.map((s, j) => (
                      <div key={j} className="badge-wrapper" tabIndex="0">
                        <span className="badge">{s.name}</span>
                        <div className="custom-tooltip">{s.desc}</div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

export default Skills;